import dayDreamImage from "@assets/images/dayDream.png";
import { useAuthStore } from "@features/authentication/stores/authStore";
import { AllMatchesSection } from "@features/messaging/components/AllMatchesSection";
import { NewMatchesSection } from "@features/messaging/components/NewMatchesSection";
import { RealtimePausedBanner } from "@features/messaging/components/RealtimePausedBanner";
import { useMatchesPage } from "@features/messaging/hooks";
import { PageLoading } from "@shared/components/PageLoading";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";

/**
 * MatchesPage component - main message hub for users
 * Features horizontal scrolling new matches and vertical chat list.
 * Rendered under ProtectedRoute; gets current user from auth store.
 */
export function MatchesPage() {
  const { userId } = useAuthStore();
  const {
    newMatches,
    allMatches,
    isLoading,
    isNewMatchesLoading,
    isAllMatchesLoading,
    error,
    realtimeError,
    handleNewMatchClick,
    handleChatClick,
    handleProfileClick,
    handleRetry,
    retryRealtime,
  } = useMatchesPage(userId ?? "");
  const [realtimeBannerDismissed, setRealtimeBannerDismissed] = useState(false);

  useEffect(() => {
    if (!realtimeError) {
      setRealtimeBannerDismissed(false);
    }
  }, [realtimeError]);

  if (!userId) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="h-full w-full bg-gray-50" data-testid="matches-page">
        <PageLoading message="Loading your matches..." className="border-lavender-400" />
      </div>
    );
  }

  // Blocking error: query / creation failures with nothing useful to show
  if (error) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50 p-8" data-testid="matches-page">
        <div className="text-center">
          <h2 className="text-xl font-semibold font-mono text-gray-900 mb-2">Something went wrong</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            type="button"
            onClick={handleRetry}
            className="bg-lavender-400 text-white px-6 py-3 rounded-lg font-semibold hover:bg-lavender-500 transition-colors"
            data-testid="retry-button"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const showRealtimeBanner = Boolean(realtimeError) && !realtimeBannerDismissed;

  // Empty state
  if ((!newMatches || newMatches.length === 0) && (!allMatches || allMatches.length === 0)) {
    return (
      <div className="flex h-full flex-col bg-gray-50" data-testid="matches-page">
        {showRealtimeBanner ? (
          <RealtimePausedBanner
            testIdPrefix="matches"
            onReconnect={retryRealtime}
            onDismiss={() => setRealtimeBannerDismissed(true)}
          />
        ) : null}
        <div className="flex flex-1 items-center justify-center p-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-center"
          >
            <img
              src={dayDreamImage}
              alt="Daydream illustration"
              className="mx-auto h-44 w-auto max-w-full"
              data-testid="no-matches-image"
            />
            <h2 className="text-xl font-semibold font-mono text-gray-900 mb-2">No matches yet</h2>
            <p className="text-gray-600 mb-6">Start swiping to find your perfect match!</p>
            <button
              type="button"
              onClick={() => window.history.back()}
              className="bg-primary text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              data-testid="start-swiping-button"
            >
              Start Swiping
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="h-full w-full bg-gray-50"
      data-testid="matches-page"
    >
      {showRealtimeBanner ? (
        <RealtimePausedBanner
          testIdPrefix="matches"
          onReconnect={retryRealtime}
          onDismiss={() => setRealtimeBannerDismissed(true)}
        />
      ) : null}

      <NewMatchesSection
        matches={newMatches || []}
        isLoading={isNewMatchesLoading}
        currentUserId={userId}
        onMatchClick={handleNewMatchClick}
      />

      <AllMatchesSection
        matches={allMatches || []}
        isLoading={isAllMatchesLoading}
        currentUserId={userId}
        onChatClick={handleChatClick}
        onProfileClick={handleProfileClick}
      />
    </motion.div>
  );
}
